import { CaptionedBlockComponent } from '@blocksuite/affine-components/caption';
import type { ColumnsBlockModel } from '@blocksuite/affine-model';
import { EDGELESS_TOP_CONTENTEDITABLE_SELECTOR } from '@blocksuite/affine-shared/consts';
import { DocModeProvider } from '@blocksuite/affine-shared/services';
import type { BlockComponent } from '@blocksuite/std';
import { html, nothing } from 'lit';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';

import {
  COLUMN_RESIZE_HANDLE_OFFSET,
  columnResizeHandleStyles,
  COLUMNS_GAP,
  columnsContainerStyles,
  columnsHostStyles,
} from './styles.js';

/** A column narrower than this is unusable, so resizing stops there. */
const MIN_COLUMN_WIDTH = 80;

type ResizeSession = {
  pointerId: number;
  index: number;
  startX: number;
  /** Grow units of the resized pair, in the order [left, right]. */
  startUnits: [number, number];
  /** Pixel widths of the resized pair when the drag started. */
  startWidths: [number, number];
  pixelsPerUnit: number;
  widths: number[];
};

/**
 * Side by side layout container. Children are `affine:column` blocks; the
 * container itself only lays them out and owns the resize grips between them.
 */
export class ColumnsBlockComponent extends CaptionedBlockComponent<ColumnsBlockModel> {
  private readonly _containerRef: Ref<HTMLDivElement> =
    createRef<HTMLDivElement>();

  private _resize: ResizeSession | null = null;

  override get topContenteditableElement() {
    if (this.std.get(DocModeProvider).getEditorMode() === 'edgeless') {
      return this.closest<BlockComponent>(
        EDGELESS_TOP_CONTENTEDITABLE_SELECTOR
      );
    }
    return this.rootComponent;
  }

  /**
   * Grow factors of the columns. An unset or stale `widths` prop means "even",
   * which keeps the layout sane when columns are added or removed elsewhere.
   */
  private get _widths(): number[] {
    const count = this.model.children.length;
    const widths = this.model.props.widths;

    if (!widths || widths.length !== count || widths.some(w => !(w > 0))) {
      return Array.from({ length: count }, () => 1);
    }

    return widths.slice();
  }

  private _getColumnElements(): HTMLElement[] {
    return this.model.children.flatMap(
      child => (this.std.view.getBlock(child.id) as HTMLElement | null) ?? []
    );
  }

  private _applyWidths(widths: number[]) {
    this._getColumnElements().forEach((element, i) => {
      element.style.flexGrow = `${widths[i] ?? 1}`;
      element.style.flexBasis = '0';
    });
  }

  private _positionHandles() {
    const container = this._containerRef.value;
    if (!container) return;

    const columns = this._getColumnElements();
    const handles = container.querySelectorAll<HTMLElement>(
      '[data-column-resize-handle]'
    );

    handles.forEach((handle, i) => {
      const left = columns[i];
      if (!left) return;
      handle.style.left = `${left.offsetLeft + left.offsetWidth + COLUMN_RESIZE_HANDLE_OFFSET}px`;
    });
  }

  private readonly _onResizeStart = (event: PointerEvent, index: number) => {
    const container = this._containerRef.value;
    const columns = this._getColumnElements();
    const left = columns[index];
    const right = columns[index + 1];
    if (!container || !left || !right || this.store.readonly) return;

    event.preventDefault();
    event.stopPropagation();

    const widths = this._widths;
    const totalUnits = widths.reduce((sum, w) => sum + w, 0);
    const available =
      container.clientWidth - COLUMNS_GAP * (columns.length - 1);
    if (available <= 0 || totalUnits <= 0) return;

    this._resize = {
      pointerId: event.pointerId,
      index,
      startX: event.clientX,
      startUnits: [widths[index], widths[index + 1]],
      startWidths: [left.offsetWidth, right.offsetWidth],
      pixelsPerUnit: available / totalUnits,
      widths,
    };

    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    (event.currentTarget as HTMLElement).dataset.resizing = 'true';
  };

  private readonly _onResizeMove = (event: PointerEvent) => {
    const session = this._resize;
    if (!session || session.pointerId !== event.pointerId) return;

    const [startLeft, startRight] = session.startWidths;
    const pairWidth = startLeft + startRight;
    const nextLeft = Math.min(
      Math.max(startLeft + (event.clientX - session.startX), MIN_COLUMN_WIDTH),
      pairWidth - MIN_COLUMN_WIDTH
    );

    const pairUnits = session.startUnits[0] + session.startUnits[1];
    const leftUnits = nextLeft / session.pixelsPerUnit;

    session.widths = session.widths.map((width, i) => {
      if (i === session.index) return leftUnits;
      if (i === session.index + 1) return pairUnits - leftUnits;
      return width;
    });

    this._applyWidths(session.widths);
    this._positionHandles();
  };

  private readonly _onResizeEnd = (event: PointerEvent) => {
    const session = this._resize;
    if (!session || session.pointerId !== event.pointerId) return;

    const handle = event.currentTarget as HTMLElement;
    handle.releasePointerCapture(event.pointerId);
    delete handle.dataset.resizing;
    this._resize = null;

    this.store.captureSync();
    this.store.updateBlock(this.model, { widths: session.widths });
  };

  private _renderResizeHandles() {
    const count = this.model.children.length;
    if (this.store.readonly || count < 2) return nothing;

    return repeat(
      Array.from({ length: count - 1 }, (_, i) => i),
      i => i,
      i => html`
        <div
          data-column-resize-handle
          class="${columnResizeHandleStyles}"
          @pointerdown=${(e: PointerEvent) => this._onResizeStart(e, i)}
          @pointermove=${this._onResizeMove}
          @pointerup=${this._onResizeEnd}
          @pointercancel=${this._onResizeEnd}
        ></div>
      `
    );
  }

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(columnsHostStyles);
  }

  override updated() {
    this._applyWidths(this._resize?.widths ?? this._widths);
    this._positionHandles();
  }

  override renderBlock() {
    return html`
      <div class="${columnsContainerStyles}" ${ref(this._containerRef)}>
        ${this.renderChildren(this.model)} ${this._renderResizeHandles()}
      </div>
    `;
  }

  override accessor useZeroWidth = true;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-columns': ColumnsBlockComponent;
  }
}
